import { useCallback, useEffect, useState } from 'react';
import type { HubClientConfig, SessionUser } from '@cortex/shared';
import { Api, clearToken, getToken, setToken } from '../lib/api';

export interface Session {
  user: SessionUser | null;
  loading: boolean;
  config: HubClientConfig | null;
  establish(token: string, user: SessionUser): void;
  signOut(): void;
}

/** Rehydrates an existing session on load (GET /api/auth/me) and loads the public hub config. */
export function useSession(): Session {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState<HubClientConfig | null>(null);

  useEffect(() => {
    Api.hub.config().then(setConfig).catch(() => setConfig({ hubUrl: null, devLoginEnabled: false, moduleKey: 'cortex' }));
    if (!getToken()) {
      setLoading(false);
      return;
    }
    Api.auth
      .me()
      .then(setUser)
      .catch(() => clearToken())
      .finally(() => setLoading(false));
    const onLost = () => setUser(null);
    window.addEventListener('cortex:session-lost', onLost);
    return () => window.removeEventListener('cortex:session-lost', onLost);
  }, []);

  const establish = useCallback((token: string, u: SessionUser) => {
    setToken(token);
    setUser(u);
  }, []);
  const signOut = useCallback(() => {
    clearToken();
    setUser(null);
  }, []);

  return { user, loading, config, establish, signOut };
}
